import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

const AdminAnnouncement = () => {
    const [formData, setFormData] = useState({
        isActive: true,
        text: 'Book your seat! Sale is live: ',
        highlightText: '30% OFF',
        link: ''
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAnnouncement();
    }, []);

    const fetchAnnouncement = async () => {
        try {
            setLoading(true);
            const response = await api.get('/announcement');
            if (response.success && response.data) {
                setFormData({
                    isActive: response.data.isActive,
                    text: response.data.text || '',
                    highlightText: response.data.highlightText || '',
                    link: response.data.link || ''
                });
            }
        } catch (err) {
            console.error('Error fetching announcement:', err);
            setError('Failed to load announcement settings.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setSaving(true);

        try {
            const response = await api.put('/announcement', formData);
            if (response.success) {
                setMessage('Announcement settings updated successfully!');
            }
        } catch (err) {
            console.error('Error updating announcement:', err);
            setError(err.message || 'Failed to update announcement settings.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <ProtectedRoute roles={['ADMIN']}>
                <DashboardLayout title="Announcement Banner" roles={['ADMIN']}>
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                </DashboardLayout>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute roles={['ADMIN']}>
            <DashboardLayout title="Announcement Banner" roles={['ADMIN']}>
                <div className="max-w-3xl bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    {message && (
                        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200">
                            {message}
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Toggle Active Status */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div>
                                <h3 className="font-medium text-gray-900">Banner Visibility</h3>
                                <p className="text-sm text-gray-500">Toggle whether the announcement banner is shown on the website.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    className="sr-only peer"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Main Text
                                </label>
                                <input
                                    type="text"
                                    name="text"
                                    value={formData.text}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. Book your seat! Sale is live: "
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Highlight Text (Bold & Yellow)
                                </label>
                                <input
                                    type="text"
                                    name="highlightText"
                                    value={formData.highlightText}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. 30% OFF"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Link URL (Optional)
                                </label>
                                <input
                                    type="text"
                                    name="link"
                                    value={formData.link}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. https://example.com/sale"
                                />
                                <p className="mt-1 text-xs text-gray-500">If provided, the banner will be clickable.</p>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-100 flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 transition-colors disabled:opacity-70"
                            >
                                {saving ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </DashboardLayout>
        </ProtectedRoute>
    );
};

export default AdminAnnouncement;
